import React, { useEffect, useState, useMemo } from 'react';
import { ArrowLeft, Loader2, RefreshCw, Download, AlertCircle } from 'lucide-react';
import { DataGrid, DataGridColumnHeader } from '@t-one-internal/design-system/lib/components/ui/data-grid';
import type { ColumnDef, SortingState, ColumnSizingState } from '@tanstack/react-table';
import { customReportsApi, type ReportDataResponse, type ReportDataQueryConfig } from '../../api/customReportsApi';
import type { CustomReportApiResponse } from './custom-builder/types';

interface ReportDataPreviewProps {
    report: CustomReportApiResponse;
    onBack: () => void;
}

const ReportDataPreview: React.FC<ReportDataPreviewProps> = ({ report, onBack }) => {
    const [data, setData] = useState<ReportDataResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

    const fetchData = async (isRefresh = false) => {
        try {
            if (isRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            setError(null);

            // Build query config from report configuration
            const queryConfig: ReportDataQueryConfig = {};

            // Add columns if specified
            if (report.columns && report.columns.length > 0) {
                queryConfig.columns = report.columns;
            }

            // Add filters if specified
            if (report.filters && report.filters.length > 0) {
                queryConfig.filters = report.filters.map(f => ({
                    field: f.field,
                    operator: f.operator,
                    value: f.value
                }));
            }

            // Add sort/order_by if specified
            if (report.order_by && report.order_by.length > 0) {
                queryConfig.order_by = report.order_by;
            }

            // Add time range if specified
            if (report.time_range) {
                queryConfig.time_range = report.time_range;
            }

            const response = await customReportsApi.getReportData(report.metric_key, queryConfig);
            setData(response);
        } catch (err) {
            console.error('Failed to fetch report data:', err);
            setError('Failed to load report data. Please try again.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [report.id]);

    // Generate columns for DataGrid from API response
    const gridColumns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
        if (!data?.columns) return [];

        return data.columns.map((col) => {
            return {
                accessorKey: col.name,
                header: ({ header, table }: { header: any; table: any }) => (
                    <DataGridColumnHeader header={header} table={table} title={col.label} />
                ),
                enableColumnFilter: col.filterable,
                enableSorting: col.sortable,
                size: col.name === 'email' ? 250 : 150,
                meta: {
                    label: col.label,
                    variant: col.data_type === 'integer' || col.data_type === 'decimal' ? 'number' : 'text'
                },
                cell: ({ getValue }: { getValue: () => unknown }) => {
                    const val = getValue();
                    if (val === null || val === undefined) {
                        return <span className="text-sm text-gray-400">-</span>;
                    }
                    const strVal = String(val);
                    // Format date/timestamp columns
                    if (col.data_type === 'date' || col.data_type === 'timestamp') {
                        try {
                            const date = new Date(strVal);
                            if (!isNaN(date.getTime())) {
                                const formatted = date.toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                });
                                return <span className="text-sm text-gray-600">{formatted}</span>;
                            }
                        } catch {
                            // Fall through to default rendering
                        }
                    }
                    // Normalize casing for status-like columns (e.g., cycle_status)
                    // If the column name contains 'status' and value is all uppercase, title-case it
                    const isStatusCol = col.name.toLowerCase().includes('status');
                    let displayVal = strVal;
                    if (isStatusCol && strVal === strVal.toUpperCase() && strVal.length > 1) {
                        displayVal = strVal.charAt(0).toUpperCase() + strVal.slice(1).toLowerCase();
                    }
                    // Truncate long decimal strings
                    displayVal = displayVal.match(/^\d+\.\d{10,}$/) ? displayVal.slice(0, 4) : displayVal;
                    // Email column gets truncation with ellipsis
                    if (col.name === 'email') {
                        return <span className="text-sm text-gray-700 block truncate max-w-[230px]" title={strVal}>{displayVal}</span>;
                    }
                    return <span className="text-sm text-gray-700">{displayVal}</span>;
                }
            };
        });
    }, [data?.columns]);

    const rows = useMemo(() => {
        return data?.data || [];
    }, [data?.data]);

    const handleExportCSV = () => {
        if (!data?.data || data.data.length === 0) return;

        const columns = data.columns.map(c => c.name);
        const headers = data.columns.map(c => c.label);

        // Build CSV content
        const csvRows = [
            headers.join(','),
            ...data.data.map(row =>
                columns.map(col => {
                    const val = row[col];
                    if (val === null || val === undefined) return '';
                    const strVal = String(val);
                    // Escape quotes and wrap in quotes if contains comma
                    if (strVal.includes(',') || strVal.includes('"') || strVal.includes('\n')) {
                        return `"${strVal.replace(/"/g, '""')}"`;
                    }
                    return strVal;
                }).join(',')
            )
        ];

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${report.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                <span className="ml-2 text-sm text-gray-500">Loading report data...</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header Toolbar */}
            <header className="flex-none bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">{report.name}</h1>
                        <p className="text-xs text-gray-500">
                            {report.report_type_name} | Live Data Preview
                        </p>
                    </div>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={() => fetchData(true)}
                        disabled={refreshing}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                    >
                        <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                    <button
                        onClick={handleExportCSV}
                        disabled={!data?.data || data.data.length === 0}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                    >
                        <Download size={16} />
                        Export CSV
                    </button>
                </div>
            </header>

            {/* Main Data Area */}
            <div className="flex-1 overflow-hidden px-6 py-6">
                {error ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
                            <p className="text-red-600 mb-4">{error}</p>
                            <button
                                onClick={() => fetchData()}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm h-full flex flex-col overflow-hidden">
                        <div className="flex-1 overflow-hidden">
                            <DataGrid
                                data={rows}
                                columns={gridColumns}
                                showToolbar={false}
                                enableRowSelection={false}
                                enableColumnResizing={true}
                                columnSizing={columnSizing}
                                onColumnSizingChange={setColumnSizing}
                                enableSorting={true}
                                sorting={sorting}
                                onSortingChange={setSorting}
                                showPagination={rows.length > 0}
                                defaultPageSize={25}
                                rowDensity="normal"
                                fullHeight={true}
                                emptyState={
                                    <div className="flex flex-col items-center justify-center py-8">
                                        <img
                                            src="/assets/EmptyTable1.png"
                                            alt="No data"
                                            className="w-40 h-40 mb-4 opacity-80"
                                        />
                                        <p className="text-gray-600 font-medium">No data found</p>
                                        <p className="text-sm text-gray-500 mt-1">Try adjusting the filters or date range in the report settings.</p>
                                    </div>
                                }
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportDataPreview;
